// Shared stamp-overlay chrome — used by both the owner and staff stamp
// flows so the two surfaces stay visually + behaviorally identical.
//
// Pieces:
//   <StampOverlayShell>      — outer .stamp-overlay/.stamp-overlay-card
//   <StampOverlaySkeleton>   — "Loading card…" with a morphing-square spinner
//   <StampOverlayBody>       — name + count + slide-to-stamp + secondary row
//   <RewardsPickerModal>     — the "🎉 has rewards" picker overlay
//   stampToastMessage()      — the post-stamp success toast copy, shared so
//                              the multi-stamp "Added X of Y" wording can't
//                              drift between the owner and staff overlays
//
// The owner and staff overlays differ only in:
//   - which API endpoint they hit for +1 stamp + redemption
//   - which TanStack queryKey they invalidate
//   - how branchId is resolved (owner: localStorage; staff: session)
// All UI lives here.

import type { ReactNode } from 'react'

import { Dropdown } from '../Dropdown.js'
import { SlideToConfirm } from '../SlideToConfirm.js'
import { formatExpiry } from '../membership/membershipTypes.js'
import {
  RedeemableMilestonesList,
  type Milestone as RedeemableMilestone,
  type SecretMenuContext,
} from '../redemption/RedemptionUI.js'

export interface StampOverlayCardData {
  card: {
    stamps_count: number
    program_snapshot: { total_stamps: number }
  }
  pending_milestones: RedeemableMilestone[]
}

// SLICE-REFERRAL — referral context shown at the counter so staff/owner know a
// customer was referred (and can thank the sharer) or has brought friends in.
// Snake_case to match the card-detail API response verbatim (no mapping).
export interface StampOverlayReferralContext {
  referred_by_name: string | null
  welcome_stamps: number
  friends_joined: number
  bonus_stamps: number
  // The next stamp on this card is a welcome-doubled first stamp — so the count
  // jumps by `welcome_multiplier`, not 1. Drives the "0 -> 2" preview + note.
  welcome_pending: boolean
  welcome_multiplier: number
}

export function StampOverlayShell({
  children,
  ariaLabelledBy = 'stamp-overlay-name',
}: {
  children: ReactNode
  ariaLabelledBy?: string
}) {
  return (
    <div
      className="stamp-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      <div className="stamp-overlay-card">{children}</div>
    </div>
  )
}

export function StampOverlaySkeleton({
  onCancel,
}: {
  onCancel: () => void
}) {
  return (
    <>
      <div className="stamp-overlay-name">Loading card…</div>
      <div className="stamp-overlay-loading">
        <span className="stamp-spinner-square" aria-hidden="true" />
      </div>
      <div className="stamp-overlay-actions">
        <button type="button" className="owner-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  )
}

/** Customer lifecycle (2026-09-05) — first-paint block for a revoked/erased
 *  customer, shown instead of the slide-to-stamp body. Card detail already
 *  carries customer.deactivated_at/erased_at (used by CustomerDetailModal's
 *  danger zone), so this is a render-time check, not a new fetch — no need
 *  to wait for a failed stamp POST to tell staff/owner why the card can't
 *  take a stamp. */
export function StampOverlayBlocked({
  customerName,
  kind,
  at,
  reason,
  onCancel,
}: {
  customerName: string
  kind: 'revoked' | 'erased'
  at: string | null
  reason?: string | null | undefined
  onCancel: () => void
}) {
  const dateStr = formatExpiry(at)
  return (
    <>
      <div id="stamp-overlay-name" className="stamp-overlay-name">
        {customerName}
      </div>
      <p className="stamp-overlay-hint">
        {kind === 'erased'
          ? `${customerName}'s data has been erased`
          : `${customerName} has been revoked`}
        {dateStr ? ` on ${dateStr}` : ''}.
        {kind === 'revoked' && reason ? ` Reason: ${reason}.` : ''}{' '}
        This card can't earn new stamps
        {kind === 'revoked' ? ' until unrevoked.' : '.'}
      </p>
      <div className="stamp-overlay-actions">
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={onCancel}
        >
          Close
        </button>
      </div>
    </>
  )
}

export function StampOverlayBody({
  data,
  customerName,
  cardCode,
  programName,
  referral,
  onCancel,
  onSlideConfirm,
  onClaimReward,
  slideDisabled = false,
  slidePending = false,
  pickerOpen = false,
  postRedeem = false,
  count = 1,
  onCountChange,
  secretCount = 0,
  onSecretCountChange,
}: {
  data: StampOverlayCardData
  customerName: string
  cardCode: string
  /** SLICE-REFERRAL — referral context for this customer, if any. Renders a
   *  small "Referred by X" / "Brought in N friends" line under the card code. */
  referral?: StampOverlayReferralContext | null
  /** The card's program name. Shown as a pill above the customer name so
   *  staff know which card they're stamping. Pass undefined to hide it (e.g.
   *  single-program tenants, where it would be redundant). */
  programName?: string | undefined
  onCancel: () => void
  onSlideConfirm: () => void
  onClaimReward: () => void
  slideDisabled?: boolean
  /** While true, the slide locks-right + shows the spinner. Owner of this
   *  flag is the parent's mutation state.
   */
  slidePending?: boolean
  /** While true (rewards picker is showing above this body) we suppress
   *  the slide+actions so the body doesn't read like "you can stamp
   *  again" while the picker is the active step. */
  pickerOpen?: boolean
  /** True once a reward has been redeemed in this overlay session. The
   *  slide stays available (customer may also have bought something) but
   *  the copy changes to prevent habit-slide accidental stamping. */
  postRedeem?: boolean
  /** Multi-stamp selector — how many stamps the next slide will add.
   *  Ignored (treated as 1) while a referral welcome-bonus stamp is
   *  pending, since that stamp's count is fixed by the referral multiplier,
   *  not staff choice. Defaults to 1 for callers that don't support it. */
  count?: number
  /** Omit to hide the stepper entirely (e.g. a surface that hasn't wired up
   *  count state). Required to actually render it. */
  onCountChange?: (n: number) => void
  /** Secret menu — how many of `count` stamps in this action are off-menu.
   *  Clamped by the caller so it never exceeds `count` (see StaffStampOverlay
   *  / StampConfirmOverlay's useEffect on `count`). Defaults to 0. */
  secretCount?: number
  /** Omit to hide the off-menu sub-stepper. Callers pass it only when the
   *  card detail's `secret_menu_adjustable.enabled` is true (secret menu
   *  live for this tenant/program). The server still independently clamps
   *  secret_count to 0 when it isn't (canTagSecretStamps). */
  onSecretCountChange?: ((n: number) => void) | undefined
}) {
  const stampsCount = data.card.stamps_count
  const totalStamps = data.card.program_snapshot.total_stamps
  const isFull = stampsCount >= totalStamps
  const roomLeft = Math.max(0, totalStamps - stampsCount)
  // A welcome-doubled first stamp lands `welcome_multiplier` stamps, not 1,
  // so the preview must show e.g. "0 -> 2" — staff can't override this one
  // (it's fixed by the referral system), so the stepper doesn't apply here.
  // The extra (welcome) stamps are applied by the server credit hook right
  // after the primary +1 commits.
  const stampAdds = referral?.welcome_pending
    ? referral.welcome_multiplier
    : Math.max(1, Math.min(count, roomLeft || 1))
  const newCount = stampsCount + stampAdds
  const pendingCount = data.pending_milestones.length
  const hasUnclaimedRewards = !isFull && pendingCount > 0
  const fullWithRewards = isFull && pendingCount > 0
  const fullNoRewards = isFull && pendingCount === 0
  // When the picker is open we render the count as a static "n / total"
  // (the current state). The `current → next` arrow only makes sense
  // when the slide is actionable.
  const showStaticCount = pickerOpen || isFull
  const showStepper =
    !showStaticCount &&
    !!onCountChange &&
    !referral?.welcome_pending &&
    roomLeft > 1
  // Secret menu — gated by the caller (see onSecretCountChange). Unlike
  // showStepper it doesn't require roomLeft > 1 — even a single stamp being
  // added can be tagged off-menu.
  const showSecretStepper =
    !showStaticCount && !!onSecretCountChange && !referral?.welcome_pending
  const slideLabel =
    stampAdds > 1 ? `Slide to add ${stampAdds} stamps` : 'Slide to add stamp'

  return (
    <>
      {programName && (
        <div className="stamp-overlay-program">{programName}</div>
      )}
      <div id="stamp-overlay-name" className="stamp-overlay-name">
        {customerName}
      </div>
      <div className="stamp-overlay-count">
        {showStaticCount ? (
          <>
            <span className="stamp-overlay-next">{stampsCount}</span>
            <span className="stamp-overlay-total">/ {totalStamps}</span>
          </>
        ) : (
          <>
            <span className="stamp-overlay-current">{stampsCount}</span>
            <span className="stamp-overlay-arrow" aria-hidden="true">
              →
            </span>
            <span className="stamp-overlay-next">{newCount}</span>
            <span className="stamp-overlay-total">/ {totalStamps}</span>
          </>
        )}
      </div>
      {showStepper && (
        <div
          className="stamp-overlay-stepper"
          role="group"
          aria-label="Number of stamps to add"
        >
          <button
            type="button"
            className="stamp-stepper-btn"
            onClick={() => onCountChange!(Math.max(1, count - 1))}
            disabled={count <= 1}
            aria-label="One fewer stamp"
          >
            −
          </button>
          {/* The count itself is a dropdown so staff can jump straight to a
              specific number (e.g. matching a physical card's existing
              stamp total) instead of tapping +1 repeatedly. */}
          <Dropdown
            className="stamp-stepper-dropdown"
            ariaLabel="Stamps to add"
            value={String(count)}
            onChange={(v) => onCountChange!(Number(v))}
            options={Array.from({ length: roomLeft }, (_, i) => {
              const n = i + 1
              return { value: String(n), label: `+${n} ${n === 1 ? 'stamp' : 'stamps'}` }
            })}
          />
          <button
            type="button"
            className="stamp-stepper-btn"
            onClick={() => onCountChange!(Math.min(roomLeft, count + 1))}
            disabled={count >= roomLeft}
            aria-label="One more stamp"
          >
            +
          </button>
        </div>
      )}

      {showSecretStepper && (
        <div
          className="stamp-overlay-secret-stepper"
          role="group"
          aria-label="Off-menu items in this order"
        >
          <span className="stamp-overlay-secret-label">
            Off-menu items in this order
          </span>
          {/* Buttons sit below the label, not crammed beside it — matches
           *  the primary stepper's touch-target size so a staff member
           *  moving fast can't miss-tap +/- against the wrong control. */}
          <div className="stamp-overlay-secret-controls">
            <button
              type="button"
              className="stamp-secret-stepper-btn"
              onClick={() =>
                onSecretCountChange!(Math.max(0, secretCount - 1))
              }
              disabled={secretCount <= 0}
              aria-label="One fewer off-menu item"
            >
              −
            </button>
            <span className="stamp-overlay-secret-count">{secretCount}</span>
            <button
              type="button"
              className="stamp-secret-stepper-btn"
              onClick={() =>
                onSecretCountChange!(Math.min(count, secretCount + 1))
              }
              disabled={secretCount >= count}
              aria-label="One more off-menu item"
            >
              +
            </button>
          </div>
        </div>
      )}

      <div className="stamp-overlay-code">{cardCode}</div>

      {referral?.welcome_pending && (
        <div className="stamp-overlay-welcome-note">
          First stamp counts {referral.welcome_multiplier}x (referral welcome
          bonus)
        </div>
      )}

      {referral && (referral.referred_by_name || referral.friends_joined > 0) && (
        <div className="stamp-overlay-referral">
          {referral.referred_by_name && (
            <div className="stamp-overlay-referral-line">
              Referred by <strong>{referral.referred_by_name}</strong>
              {referral.welcome_stamps > 0 &&
                `, got +${referral.welcome_stamps} welcome ${
                  referral.welcome_stamps === 1 ? 'stamp' : 'stamps'
                }`}
            </div>
          )}
          {referral.friends_joined > 0 && (
            <div className="stamp-overlay-referral-line">
              Referred <strong>{referral.friends_joined}</strong>{' '}
              {referral.friends_joined === 1 ? 'friend' : 'friends'}
              {referral.bonus_stamps > 0 &&
                `, earned +${referral.bonus_stamps} bonus ${
                  referral.bonus_stamps === 1 ? 'stamp' : 'stamps'
                }`}
            </div>
          )}
        </div>
      )}

      {fullWithRewards && !pickerOpen && (
        <p className="stamp-overlay-hint">
          Card is full — {pendingCount} {pendingCount === 1 ? 'reward' : 'rewards'}{' '}
          still waiting to be claimed.
        </p>
      )}
      {fullNoRewards && (
        <p className="stamp-overlay-hint">
          Card is complete — all rewards already claimed.
        </p>
      )}

      {pickerOpen ? null : isFull ? (
        <div className="stamp-overlay-actions">
          <button type="button" className="owner-btn" onClick={onCancel}>
            Close
          </button>
          {/* Skipping the auto-opened picker closes it (pickerOpen flips to
              false) but the rewards are still sitting unclaimed on a full
              card — give staff a way back in rather than forcing a
              close-and-rescan round trip. */}
          {fullWithRewards && (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={onClaimReward}
            >
              View rewards
            </button>
          )}
        </div>
      ) : postRedeem ? (
        // Post-redeem branch: the happy path is the customer just claimed a
        // reward and is done. Make Done the primary action; the slider becomes
        // an explicit "no really, ALSO add a stamp because they bought
        // something" secondary affordance. Mitigates the accidental
        // re-stamp class of error.
        <div className="stamp-overlay-stack">
          <p className="stamp-overlay-hint">
            Reward redeemed. Tap Done if no purchase was made — slide below
            only if the customer also bought something today.
          </p>
          <button
            type="button"
            className="owner-btn owner-btn-primary owner-btn-block stamp-overlay-done-primary"
            onClick={onCancel}
          >
            Done
          </button>
          <SlideToConfirm
            label={
              stampAdds > 1
                ? `Slide to also add ${stampAdds} stamps`
                : 'Slide to also add a stamp'
            }
            confirmingLabel="Stamping…"
            onConfirm={onSlideConfirm}
            disabled={slideDisabled}
            pending={slidePending}
          />
        </div>
      ) : (
        <div className="stamp-overlay-stack">
          <SlideToConfirm
            label={slideLabel}
            confirmingLabel="Stamping…"
            onConfirm={onSlideConfirm}
            disabled={slideDisabled}
            pending={slidePending}
          />
          <div className="stamp-overlay-secondary-row">
            {/* Cancel stays clickable even while a stamp is in flight. The
                stamp POST can't be un-sent, but the owner shouldn't be
                trapped staring at a frozen overlay for the length of a slow
                round-trip — closing just dismisses the UI; the stamp (if it
                landed) is idempotent and the count refreshes via the
                invalidate. Only the slide + Claim are locked during pending,
                to prevent a double-stamp / mid-stamp redeem. */}
            <button
              type="button"
              className="owner-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
            {hasUnclaimedRewards && (
              <button
                type="button"
                className="owner-btn stamp-overlay-claim-btn"
                onClick={onClaimReward}
                disabled={slidePending}
              >
                Claim reward
                <span
                  className="stamp-overlay-claim-badge"
                  aria-label={`${pendingCount} pending`}
                >
                  {pendingCount}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/** Multi-stamp selector — the success toast for a stamp mutation. Falls
 *  back to the pre-existing singular copy when the batch wasn't capped and
 *  wasn't a multi-stamp action, so single-stamp behavior is unchanged.
 *  Secret menu — appends "(N off-menu)" when the batch tagged any stamps
 *  secret; omitted entirely (and byte-identical to before) otherwise. */
export function stampToastMessage(
  customerName: string,
  finalCount: number,
  totalStamps: number,
  result: {
    requested_count: number
    stamps_added: number
    secret_stamps_added?: number
  },
): string {
  const secretSuffix =
    result.secret_stamps_added && result.secret_stamps_added > 0
      ? ` (${result.secret_stamps_added} off-menu)`
      : ''
  if (result.stamps_added < result.requested_count) {
    return (
      `Added ${result.stamps_added} of ${result.requested_count} stamps${secretSuffix} · ` +
      `${customerName} · ${finalCount} / ${totalStamps} — card is now full`
    )
  }
  const label =
    result.stamps_added > 1 ? `${result.stamps_added} stamps added` : 'Stamp added'
  return `${label}${secretSuffix} · ${customerName} · ${finalCount} / ${totalStamps}`
}

export function RewardsPickerModal({
  name,
  milestones,
  onRedeem,
  onSkip,
  secretMenuContext,
}: {
  name: string
  milestones: RedeemableMilestone[]
  onRedeem: (m: RedeemableMilestone) => void
  onSkip: () => void
  secretMenuContext?: SecretMenuContext | null
}) {
  return (
    <div
      className="stamp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rewards-panel-title"
    >
      <div className="stamp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stamp-modal-icon" aria-hidden="true">
          🎉
        </div>
        <h2 id="rewards-panel-title" className="stamp-modal-title">
          {name} has{' '}
          {milestones.length === 1
            ? 'a reward'
            : `${milestones.length} rewards`}!
        </h2>
        <RedeemableMilestonesList
          milestones={milestones}
          onRedeem={onRedeem}
          secretMenuContext={secretMenuContext ?? null}
        />
        <button
          type="button"
          className="owner-btn"
          onClick={onSkip}
          style={{ marginTop: 16 }}
        >
          Skip for now
        </button>
      </div>
    </div>
  )
}
